import {fomanticQuery} from '../modules/fomantic/base.ts';
import {localUserSettings} from '../modules/user-settings.ts';
import {toggleElem} from '../utils/dom.ts';

const settingKey = 'pull-commit-status-filter';
const filters = ['all', 'hide-success', 'hide-success-skipped'] as const;

export type CommitStatusFilter = typeof filters[number];

export function getCommitStatusFilter(): CommitStatusFilter {
  const value = localUserSettings.getString(settingKey, 'hide-success-skipped');
  return (filters as readonly string[]).includes(value) ? value as CommitStatusFilter : 'all';
}

export function isCommitStatusHidden(status: string, filter: CommitStatusFilter): boolean {
  if (filter === 'all') return false;
  if (status === 'success') return true;
  return filter === 'hide-success-skipped' && status === 'skipped';
}

export function applyCommitStatusFilter(box: Element, filter: CommitStatusFilter) {
  let shownCount = 0;
  const items = box.querySelectorAll('.commit-status-list > .commit-status-item');
  for (const item of items) {
    const shown = !isCommitStatusHidden(item.getAttribute('data-commit-status') ?? '', filter);
    toggleElem(item, shown);
    if (shown) shownCount++;
  }

  const rollup = box.querySelector<HTMLElement>('.commit-status-rollup');
  if (rollup) {
    const completed = [...items].filter((item) => ['success', 'skipped'].includes(item.getAttribute('data-commit-status')!));
    toggleElem(rollup, completed.length > 0);
    const expanded = completed.some((item) => !item.classList.contains('tw-hidden'));
    const button = rollup.querySelector<HTMLButtonElement>('.commit-status-completed-toggle')!;
    button.setAttribute('aria-expanded', String(expanded));
    button.textContent = button.getAttribute(expanded ? 'data-hide' : 'data-show');
    const counts = box.querySelectorAll('.commit-status-count[data-check-status="successful"], .commit-status-count[data-check-status="skipped"]');
    rollup.querySelector('.commit-status-rollup-summary')!.textContent = [...counts].map((count) => count.textContent.trim()).join(' · ');
  }

  // Explains an empty list when the filter hides every check.
  const elEmpty = box.querySelector<HTMLElement>('.commit-status-list > .commit-status-filter-empty');
  if (!elEmpty) return;
  const isEmpty = items.length > 0 && shownCount === 0 && !rollup;
  if (isEmpty) {
    elEmpty.querySelector('.commit-status-filter-empty-text')!.textContent = elEmpty.getAttribute(`data-text-${filter}`);
  }
  toggleElem(elEmpty, isEmpty);
}

export function initRepoPullCommitStatusFilter(box: HTMLElement) {
  const elDropdown = box.querySelector('.commit-status-filter');
  if (!elDropdown) return;

  const list = box.querySelector('.pull-checks-list');
  if (list) {
    const priority: Record<string, number> = {failure: 0, error: 0, blocked: 0, warning: 0, running: 1, pending: 2, waiting: 2, missing: 2, cancelled: 3, cancelling: 3, success: 4, skipped: 5};
    const items = [...list.querySelectorAll<HTMLElement>(':scope > .commit-status-item')];
    items.sort((a, b) => (priority[a.getAttribute('data-commit-status')!] ?? 2) - (priority[b.getAttribute('data-commit-status')!] ?? 2));
    const empty = list.querySelector('.commit-status-filter-empty');
    for (const item of items) list.insertBefore(item, empty);
    const progress = box.querySelector<HTMLElement>('.commit-status-progress');
    if (progress) {
      const terminal = new Set(['success', 'skipped', 'failure', 'error', 'cancelled', 'warning']);
      const finished = items.filter((item) => terminal.has(item.getAttribute('data-commit-status')!)).length;
      const label = `${finished} / ${items.length} ${progress.getAttribute('data-label')}`;
      const track = progress.querySelector<HTMLElement>('.commit-status-progress-track')!;
      track.setAttribute('aria-label', label);
      track.setAttribute('aria-valuenow', String(finished));
      track.setAttribute('aria-valuemax', String(items.length));
      const groups = new Map<string, number>();
      for (const item of items) {
        const status = item.getAttribute('data-commit-status')!;
        groups.set(status, (groups.get(status) || 0) + 1);
      }
      track.replaceChildren(...[...groups].map(([status, count]) => {
        const segment = document.createElement('span');
        segment.setAttribute('data-commit-status', status);
        segment.style.flexGrow = String(count);
        return segment;
      }));
      toggleElem(progress, items.length > 0);
    }
  }

  const filter = getCommitStatusFilter();
  applyCommitStatusFilter(box, filter);

  const $dropdown = fomanticQuery(elDropdown);
  $dropdown.dropdown();
  $dropdown.dropdown('set selected', filter);
  // Add the callback after the initial selection so that it only runs for user changes.
  $dropdown.dropdown('setting', 'onChange', (value: string) => {
    const filter = (filters as readonly string[]).includes(value) ? value as CommitStatusFilter : 'all';
    localUserSettings.setString(settingKey, filter);
    applyCommitStatusFilter(box, filter);
  });

  box.querySelector('.commit-status-completed-toggle')?.addEventListener('click', () => {
    const expanded = box.querySelector('.commit-status-completed-toggle')!.getAttribute('aria-expanded') === 'true';
    $dropdown.dropdown('set selected', expanded ? 'hide-success-skipped' : 'all');
  });

  box.querySelector('.commit-status-filter-show-all')?.addEventListener('click', () => {
    $dropdown.dropdown('set selected', 'all');
  });
}
